import { z } from 'zod'

import { searchTextParamSchema } from '@/schemas/common'
import { gatewaySchema } from '@/schemas/payments'

/**
 * Route search schemas for the subscription flow. Out of the screen files
 * because a schema there can only be imported alongside `createFileRoute`,
 * which no test may load (see `screens/brokers/search.ts`).
 *
 * `/payment/return` and `/payment/failed` deliberately have NO schema. A
 * gateway's query string is read raw by
 * `features/payments/lib/returnParams.ts`, and TanStack keeps every key it
 * was not told about — its `buildLocation` merges a route's validated search
 * over the parsed one — so leaving them unvalidated drops nothing. It cannot
 * stop the router re-encoding the URL on mount; `returnParams.ts` undoes that.
 */

/**
 * `/subscription/checkout?plan=ID[&gateway=…][&code=…]`. Nothing here fails
 * the page: a missing or garbled `plan` reaches the screen as `undefined`,
 * which answers with a way back to the plans, and a gateway or code that does
 * not parse is dropped — the member picks again.
 *
 * `code` also takes a boolean: the router's codec JSON-parses every value, so
 * a pasted `?code=true` arrives as one. What the codec rewrites beyond that
 * (`1E5` → 100000) the screen recovers from the URL it was opened with.
 */
export const checkoutSearchSchema = z.object({
  plan: z.number().int().positive().optional().catch(undefined),
  gateway: gatewaySchema.optional().catch(undefined),
  code: z
    .union([searchTextParamSchema, z.boolean().transform(String)])
    .optional()
    .catch(undefined),
})

export type CheckoutSearch = z.infer<typeof checkoutSearchSchema>

/**
 * `?billing=true` opens the Billing history dialog over Manage plan (owner C6: the mock's dialog,
 * not a page). The old `/subscription/billing` URL redirects here; a member on Free (who can still
 * have a pending or cancelled attempt on file) lands on `/subscription` with the same flag.
 */
export const billingSearchSchema = z.object({
  billing: z.boolean().optional().catch(undefined),
})

export type BillingSearch = z.infer<typeof billingSearchSchema>
